import { Platform, Pressable, ScrollView, Text, View } from "react-native";

import type { ClientCompetitionListRow } from "../../types/database";
import { COLORS, FONTS } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";

const THEME = PANE_THEMES.Client;
const AMBER = THEME.accent;
const FOREST = THEME.sidebarBg;
const FOREST_MID = COLORS.FOREST_MID.hex;
const LINEN = THEME.textOnSurface;
const SAGE = COLORS.SAGE.hex;
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";

// TODO: wire this to the actual assigned account-manager email when that
// data lands in the schema. For now every RM program points at the team
// inbox.
const RM_ACCOUNT_MANAGER_EMAIL = "rm-team@coravu.com";

export function RmStatusBody({ row }: { row: ClientCompetitionListRow }) {
  const status = row.status;
  const opensAt = row.opens_at ? new Date(row.opens_at) : null;
  const closesAt = row.closes_at ? new Date(row.closes_at) : null;

  const fmt = (d: Date | null) =>
    d
      ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
      : "TBC";

  const daysToClose = (() => {
    if (!closesAt) return null;
    const ms = closesAt.getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86_400_000));
  })();

  // Approval-flow states (submitted/approved/etc) aren't user-facing here
  // — RM clients reach this body via /client/competition/[id] only after
  // their case is live. We still type as Partial so the codepath compiles
  // if a pre-active row ever leaks through, and fall back to a neutral
  // copy string.
  const statusCopy: Partial<Record<typeof status, string>> = {
    draft: "Coravu is preparing your program. We'll notify you when registration opens.",
    open: "Registration is open. Students are joining.",
    judging: "Judging is in progress. Coravu's panel is reviewing every submission.",
    closed: "This program is complete. Top performers are below.",
  };
  const statusCopyText = statusCopy[status] ?? "Coravu is setting up your program.";

  const statusLabel = (() => {
    switch (status) {
      case "draft": return "Draft";
      case "open": return "Open";
      case "judging": return "Judging";
      case "closed": return "Closed";
      default: return "Pending";
    }
  })();

  const statusColor = (() => {
    switch (status) {
      case "open": return SAGE;
      case "judging": return AMBER;
      case "closed": return WM;
      case "draft":
      default: return WL;
    }
  })();

  return (
    <ScrollView contentContainerStyle={{ paddingBottom: 80 }}>
      <View style={{ paddingHorizontal: 32, paddingVertical: 28, maxWidth: 980 }}>
        {/* Hero */}
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.displaySemiBold,
              fontSize: 10,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Program Status
          </Text>
        </View>
        <Text
          style={{
            marginTop: 8,
            fontFamily: FONTS.display,
            fontSize: 30,
            lineHeight: 36,
            color: LINEN,
          }}
        >
          {row.name}
        </Text>
        <View
          style={{
            marginTop: 8,
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          {row.company_name ? (
            <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: WM }}>
              {row.company_name}
            </Text>
          ) : null}
          <View
            style={{
              paddingHorizontal: 8,
              paddingVertical: 2,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: statusColor,
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9.5,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: statusColor,
              }}
            >
              {statusLabel}
            </Text>
          </View>
          <View
            style={{
              paddingHorizontal: 8,
              paddingVertical: 2,
              borderRadius: 999,
              backgroundColor: "rgba(201,162,74,0.18)",
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9.5,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: COLORS.GOLD_RM.hex,
              }}
            >
              Coravu Managed
            </Text>
          </View>
        </View>

        {/* Status banner */}
        <View
          style={{
            marginTop: 22,
            padding: 18,
            borderRadius: 6,
            backgroundColor: FOREST_MID,
            borderWidth: 1,
            borderColor: BORDER,
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyMedium,
              fontSize: 13.5,
              lineHeight: 22,
              color: LINEN,
            }}
          >
            {statusCopyText}
          </Text>
          {opensAt || closesAt ? (
            <Text
              style={{
                marginTop: 8,
                fontFamily: FONTS.bodyLight,
                fontSize: 12,
                color: WM,
              }}
            >
              Opens {fmt(opensAt)} · Closes {fmt(closesAt)}
            </Text>
          ) : null}
        </View>

        {/* Stats strip — 4 tiles */}
        <View style={{ marginTop: 22, flexDirection: "row", flexWrap: "wrap", gap: 12 }}>
          <RmStatTile label="Registered students" value={String(row.registration_count ?? 0)} color={LINEN} />
          <RmStatTile label="Submissions received" value={String(row.submission_count ?? 0)} color={AMBER} />
          <RmStatTile
            label="Days to closing"
            value={daysToClose == null ? "—" : String(daysToClose)}
            color={SAGE}
          />
          <RmStatTile label="Shortlisted" value="—" color={SAGE} />
        </View>

        {/* Brief */}
        {row.brief ? (
          <View
            style={{
              marginTop: 22,
              padding: 22,
              borderRadius: 6,
              backgroundColor: FOREST_MID,
              borderWidth: 1,
              borderColor: BORDER,
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9.5,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: AMBER,
                marginBottom: 8,
              }}
            >
              Brief
            </Text>
            <Text
              style={{
                fontFamily: FONTS.bodyLight,
                fontSize: 14,
                lineHeight: 26,
                color: "rgba(255,255,255,0.78)",
              }}
            >
              {row.brief}
            </Text>
          </View>
        ) : null}

        {/* Account manager CTA */}
        <View
          style={{
            marginTop: 22,
            padding: 22,
            borderRadius: 6,
            backgroundColor: FOREST,
            borderLeftWidth: 4,
            borderLeftColor: AMBER,
            borderTopWidth: 1,
            borderRightWidth: 1,
            borderBottomWidth: 1,
            borderTopColor: BORDER,
            borderRightColor: BORDER,
            borderBottomColor: BORDER,
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: AMBER,
              marginBottom: 6,
            }}
          >
            Need a change?
          </Text>
          <Text
            style={{
              fontFamily: FONTS.displayMedium,
              fontSize: 22,
              color: LINEN,
              marginBottom: 8,
            }}
          >
            Talk to your account manager.
          </Text>
          <Text
            style={{
              fontFamily: FONTS.bodyLight,
              fontSize: 13,
              lineHeight: 22,
              color: WM,
              marginBottom: 14,
            }}
          >
            Coravu owns the case design, deliverable structure, and judging. To request a change to your program, contact your account manager.
          </Text>
          <Pressable
            onPress={() => {
              if (Platform.OS === "web" && typeof window !== "undefined") {
                window.location.href = `mailto:${RM_ACCOUNT_MANAGER_EMAIL}`;
              }
            }}
            style={{
              alignSelf: "flex-start",
              paddingHorizontal: 16,
              paddingVertical: 10,
              borderRadius: 4,
              backgroundColor: AMBER,
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 11,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: FOREST,
              }}
            >
              Email account manager →
            </Text>
          </Pressable>
        </View>
      </View>
    </ScrollView>
  );
}

function RmStatTile({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <View
      style={{
        flexBasis: ("calc(25% - 9px)" as unknown) as number,
        flexGrow: 1,
        minWidth: 180,
        height: 76,
        paddingHorizontal: 16,
        paddingVertical: 12,
        backgroundColor: "rgba(28,43,26,0.40)",
        borderWidth: 1,
        borderColor: BORDER,
        borderRadius: 4,
        justifyContent: "space-between",
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: WM,
        }}
      >
        {label}
      </Text>
      <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color }}>
        {value}
      </Text>
    </View>
  );
}
